/**
 * Popup state persistence layer using localStorage.
 *
 * Tracks per-popup state (dismissed, converted) and enforces
 * suppression rules: 14-day suppression after dismissal,
 * permanent suppression after conversion.
 */

const STORAGE_KEY = 'wayra_popup_state';
const SUPPRESSION_DAYS = 14;
const MS_PER_DAY = 86_400_000;

interface PopupRecord {
  dismissedAt: number | null;
  convertedAt: number | null;
  shownAt: number | null;
}

interface PopupStateData {
  popups: Record<string, PopupRecord>;
  /** Timestamp of the first-ever visit (used for "new visitor" checks) */
  firstVisitAt: number;
}

const DEFAULT_STATE = (): PopupStateData => ({
  popups: {},
  firstVisitAt: Date.now(),
});

/** Type guard to validate that a parsed JSON value has the expected shape. */
function isPopupStateData(value: unknown): value is PopupStateData {
  if (typeof value !== 'object' || value === null) return false;
  if (
    !('popups' in value) ||
    typeof value.popups !== 'object' ||
    value.popups === null
  )
    return false;
  if (!('firstVisitAt' in value) || typeof value.firstVisitAt !== 'number')
    return false;
  return true;
}

/**
 * Read the full popup state from localStorage.
 * Returns a default state if nothing is stored yet or the shape is invalid.
 */
function readState(): PopupStateData {
  if (typeof window === 'undefined') {
    return DEFAULT_STATE();
  }

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return DEFAULT_STATE();
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return isPopupStateData(parsed) ? parsed : DEFAULT_STATE();
  } catch {
    return DEFAULT_STATE();
  }
}

/**
 * Write popup state to localStorage.
 * Silently no-ops on QuotaExceededError to prevent app crashes.
 */
function writeState(state: PopupStateData): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage quota exceeded — state won't persist but app continues normally
  }
}

/**
 * Ensure a first-visit timestamp is recorded.
 * Call this on app mount so "new visitor" logic works.
 */
function ensureFirstVisit(): void {
  if (typeof window === 'undefined') return;
  const state = readState();
  if (!localStorage.getItem(STORAGE_KEY)) {
    writeState(state);
  }
}

/**
 * Returns true if the visitor has never visited before this session
 * (i.e., no popup state existed in localStorage before this page load).
 */
function isFirstVisit(): boolean {
  if (typeof window === 'undefined') return false;
  return !localStorage.getItem(STORAGE_KEY);
}

/**
 * Mark a popup as dismissed (suppresses all popups for 14 days).
 */
function recordDismissal(popupId: string): void {
  const state = readState();
  const existing = state.popups[popupId];
  state.popups[popupId] = {
    dismissedAt: Date.now(),
    convertedAt: existing?.convertedAt ?? null,
    shownAt: existing?.shownAt ?? null,
  };
  writeState(state);
}

/**
 * Mark a popup as converted (suppresses all popups permanently).
 */
function recordConversion(popupId: string): void {
  const state = readState();
  const existing = state.popups[popupId];
  state.popups[popupId] = {
    dismissedAt: existing?.dismissedAt ?? null,
    convertedAt: Date.now(),
    shownAt: existing?.shownAt ?? null,
  };
  writeState(state);
}

/**
 * Record that a popup was shown to the visitor.
 * Persists across sessions so the popup won't reappear even
 * if the visitor closes the tab without interacting.
 */
function recordShown(popupId: string): void {
  const state = readState();
  const existing = state.popups[popupId];
  state.popups[popupId] = {
    dismissedAt: existing?.dismissedAt ?? null,
    convertedAt: existing?.convertedAt ?? null,
    shownAt: Date.now(),
  };
  writeState(state);
}

/**
 * Returns true if a popup has been shown to the visitor before.
 */
function hasBeenShown(popupId: string): boolean {
  const state = readState();
  const record = state.popups[popupId];
  return record?.shownAt !== null && record?.shownAt !== undefined;
}

/**
 * Returns true if any globally-managed popup has been converted (permanent suppression).
 */
function hasConverted(): boolean {
  const state = readState();
  return Object.values(state.popups).some((p) => p.convertedAt !== null);
}

/**
 * Returns true if any globally-managed popup was dismissed within the suppression window.
 */
function isSuppressed(): boolean {
  const state = readState();
  const now = Date.now();
  const cutoff = now - SUPPRESSION_DAYS * MS_PER_DAY;

  return Object.values(state.popups).some(
    (p) => p.dismissedAt !== null && p.dismissedAt > cutoff
  );
}

/**
 * Returns true if any globally-managed popup has been shown to the visitor
 * (regardless of whether they interacted with it).
 */
function hasBeenShownAny(): boolean {
  const state = readState();
  return Object.values(state.popups).some((p) => p.shownAt != null);
}

/**
 * Returns true if popups should be blocked — from a conversion
 * (permanent), a recent dismissal (14 days), or having already
 * been shown (prevents re-show if visitor closed tab without interacting).
 */
function isBlocked(): boolean {
  return hasConverted() || isSuppressed() || hasBeenShownAny();
}

export const popupState = {
  ensureFirstVisit,
  isFirstVisit,
  recordDismissal,
  recordConversion,
  recordShown,
  hasBeenShown,
  hasConverted,
  isSuppressed,
  isBlocked,
};
